import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ConsumerRightsForm } from "@/components/forms/ConsumerRightsForm";

export const Route = createFileRoute("/consumer-rights-request")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/consumer-rights-request/" }],
    meta: [
      { title: "Consumer Rights Request | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Submit a data subject request to access, correct, or delete your personal information, opt out of sales, or limit use of sensitive data.",
      },
      { property: "og:title", content: "Consumer Rights Request — RESTORE® Hair" },
      {
        property: "og:description",
        content:
          "Data subject request form for accessing, correcting, deleting, or limiting the use of your personal information.",
      },
    ],
  }),
  component: ConsumerRightsPage,
});

function ConsumerRightsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="bg-background py-10 md:py-16">
          <div className="mx-auto max-w-3xl px-4">
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Data Subject Request Concerning Personal Information
            </h1>

            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                Please use this form to submit requests to access, correct, or delete your
                personal information; to opt-out of the sale of your personal information or
                disclosure of that information for targeted advertising; or to limit the use or
                disclosure of your sensitive personal information. We may ask you for additional
                information to verify your identity and/or authority to make this request. If we
                ask for proof of identity, please send any sensitive information by secure
                means.
              </p>
              <p>
                We will endeavor to respond to your request promptly. In general, we will
                respond within 45 calendar days from the date we receive your request. If we
                need additional time, however, we will let you know within the first 45
                calendar days following the date we receive your request. Please note that
                certain conditions may limit our ability to comply with your request and, under
                certain limited circumstances, we may charge a reasonable fee for responding to
                your request.
              </p>
              <p>
                To submit your request, please complete this form and click "Submit" below. To
                submit your request by phone, please call us toll-free at{" "}
                <a href="tel:+18664889356" className="text-primary underline" suppressHydrationWarning>
                  (866) 488-9356
                </a>
                . If we need additional information to process your request, we will generally
                notify you using the mechanism you designate below. However, we may instead
                respond by email or regular mail, where applicable. We will use any information
                we collect for verification purposes solely for that purpose.
              </p>
            </div>

            <div className="mt-10 rounded-lg border border-border bg-card p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-foreground">
                Consumer Rights Request
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                If you are the data subject or are submitting a request on behalf of the data
                subject, please provide the following information about the data subject.
              </p>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                NOTE: If you are a data subject and are only submitting an opt-out or limit
                use/disclosure request (i.e., you are not also requesting to access, correct,
                or delete your personal information), you only need to provide us, in your
                initial submission, your name and relationship to us. You can leave the
                remaining fields blank. If you are submitting a request on behalf of the data
                subject, you will also need to provide your name and relationship to the data
                subject.
              </p>

              <div className="mt-8">
                <ConsumerRightsForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}